import { Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { PipAlignment } from '@/features/picture-in-picture/anchor-bridge';
import { usePipStill } from '@/features/picture-in-picture/pip-stills';
import { electronInvoke } from '@/lib/electron-bridge';

/** An item of the picture-in-picture, as the Desktop lists it. */
export type PipItem = {
  id: string;
  conversationId: string;
  title: string;
  state: 'live' | 'completed';
  kind: 'browser' | 'computer';
  /** A Mac window filmed live (`window:<id>:0`); otherwise the item's stills. */
  capture: string | null;
  /** The Mac app's icon, shown as its turn ends. */
  icon: string | null;
  /** Its content's width over height, once known. */
  aspect: number | null;
};

const reportAspect = (id: string, width: number, height: number) => {
  if (width > 0 && height > 0)
    void electronInvoke('pip_aspect', { id, aspect: width / height }).catch(() => undefined);
};

/** The item's latest still. */
function PipStill({ id, label }: { id: string; label: string }) {
  const url = usePipStill(id);
  if (url === undefined) return <div className="absolute inset-0 bg-neutral-900" />;
  return (
    <img
      alt={label}
      className="absolute inset-0 size-full object-cover select-none"
      draggable={false}
      src={url}
    />
  );
}

/**
 * A Mac window filmed live (Chromium's desktop capture of that window). Falls back to the stills
 * when it cannot be filmed (closed, not allowed).
 */
function PipLiveWindow({ capture, id }: { capture: string; id: string }) {
  const video = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    let stream: MediaStream | null = null;
    let disposed = false;
    void navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: {
          mandatory: {
            chromeMediaSource: 'desktop',
            chromeMediaSourceId: capture,
            maxFrameRate: 15,
          },
        } as MediaTrackConstraints,
      })
      .then((captured) => {
        if (disposed) {
          for (const track of captured.getTracks()) track.stop();
          return;
        }
        stream = captured;
        for (const track of captured.getTracks())
          track.addEventListener('ended', () => setFailed(true));
        if (video.current) video.current.srcObject = captured;
      })
      .catch(() => {
        if (!disposed) setFailed(true);
      });
    return () => {
      disposed = true;
      for (const track of stream?.getTracks() ?? []) track.stop();
    };
  }, [capture]);
  if (failed) return <PipStill id={id} label="Computer Use preview" />;
  return (
    <video
      ref={video}
      aria-label="Computer Use preview"
      autoPlay
      className="absolute inset-0 size-full object-cover"
      disablePictureInPicture
      muted
      onLoadedMetadata={(event) =>
        reportAspect(id, event.currentTarget.videoWidth, event.currentTarget.videoHeight)
      }
      playsInline
    />
  );
}

/**
 * What an item shows, filling its box: a Mac window filmed live, or the latest still (a tab after
 * the agent's last action, the driver's screenshot of a window), as Codex's previews.
 */
export function PipMedia({ item }: { item: PipItem }) {
  return item.capture === null ? (
    <PipStill
      id={item.id}
      label={item.kind === 'browser' ? 'Browser Use preview' : 'Computer Use preview'}
    />
  ) : (
    <PipLiveWindow capture={item.capture} id={item.id} />
  );
}

/**
 * The end of a finished turn, as Codex's completion effect: a scrim and the app's icon fade in, a
 * check springs in, then shrinks into a badge on the icon; the item stays so for 30 s.
 */
export function PipCompleted({ icon }: { icon: string | null }) {
  return (
    <div className="pointer-events-none absolute inset-0 grid place-items-center">
      <div className="pip-complete-scrim absolute inset-0 bg-black" />
      <div className="relative grid place-items-center">
        {icon !== null && (
          <img
            alt=""
            className="pip-complete-icon size-12 drop-shadow"
            draggable={false}
            src={icon}
          />
        )}
        <span
          className={`pip-complete-check grid size-8 place-items-center rounded-full bg-emerald-500 text-white shadow-md ${
            icon === null ? '' : 'absolute'
          }`}
        >
          <Check className="size-5" strokeWidth={3} />
        </span>
      </div>
    </div>
  );
}

/** The exit animation (in step with the Desktop's hide delay). */
const EXIT_MS = 260;

export type RenderedPipItem = PipItem & { exiting: boolean };

/** Whether two rendered items show the same: every field of theirs is the same value. */
function sameRenderedItem(left: RenderedPipItem, right: RenderedPipItem): boolean {
  const keys = Object.keys(left) as (keyof RenderedPipItem)[];
  return keys.length === Object.keys(right).length && keys.every((key) => left[key] === right[key]);
}

/**
 * The items shown, with those that left kept for their exit. Its state changes only when what it
 * shows does: callers pass a new list each render (an empty `[]` when nothing shows), and setting
 * a new one each time would render forever.
 */
export function useRenderedPipItems(items: PipItem[]): RenderedPipItem[] {
  const [rendered, setRendered] = useState<RenderedPipItem[]>([]);
  // What was shown, and whether some item still waits for the end of its exit.
  const shown = useRef<readonly string[]>([]);
  const exiting = useRef(false);
  useEffect(() => {
    const ids = new Set(items.map((item) => item.id));
    if (shown.current.some((id) => !ids.has(id))) exiting.current = true;
    shown.current = items.map((item) => item.id);
    setRendered((current) => {
      const leaving = current
        .filter((item) => !ids.has(item.id))
        .map((item) => ({ ...item, exiting: true }));
      const next = [...items.map((item) => ({ ...item, exiting: false })), ...leaving];
      return next.length === current.length &&
        next.every((item, index) => sameRenderedItem(item, current[index] as RenderedPipItem))
        ? current
        : next;
    });
    if (!exiting.current) return;
    const timer = setTimeout(() => {
      exiting.current = false;
      setRendered((current) => current.filter((item) => !item.exiting));
    }, EXIT_MS);
    return () => clearTimeout(timer);
  }, [items]);
  return rendered;
}

/** How far each item behind the lead steps toward the inside (Codex's 28 × 22). */
export const PIP_CASCADE = { x: 28, y: 22 } as const;

/** Where the `index`th item sits from the lead. */
function pipCascade(alignment: PipAlignment, index: number): { x: number; y: number } {
  return {
    x: (alignment.endsWith('right') ? -1 : 1) * PIP_CASCADE.x * index,
    y: (alignment.startsWith('bottom') ? -1 : 1) * PIP_CASCADE.y * index,
  };
}

/** An item's size: its content's aspect (16:10 until known) fitted in `displaySize`'s square. */
export function pipItemSize(
  displaySize: number,
  aspect: number | null,
): { width: number; height: number } {
  const ratio = aspect !== null && aspect > 0 ? Math.min(Math.max(aspect, 0.25), 4) : 16 / 10;
  return ratio >= 1
    ? { width: displaySize, height: Math.round(displaySize / ratio) }
    : { width: Math.round(displaySize * ratio), height: displaySize };
}

/** The `index`th item's box, its `alignment` corner at the lead's corner moved by the cascade. */
export function pipItemBox(
  anchor: { x: number; y: number },
  alignment: PipAlignment,
  index: number,
  size: { width: number; height: number },
): { left: number; top: number; width: number; height: number } {
  const offset = pipCascade(alignment, index);
  const x = anchor.x + offset.x;
  const y = anchor.y + offset.y;
  return {
    left: alignment.endsWith('left') ? x : x - size.width,
    top: alignment.startsWith('top') ? y : y - size.height,
    ...size,
  };
}
